import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import Checkbox from '@repo/ui/checkbox'
import { EuroIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { getCountries } from '../../../api/countries/get-countries'
import { getTimezones } from '../../../api/timezones/get-timezones'
import {
  AccomodationEnum,
  accomodationEnumMapping
} from '../../../lib/types/enums/accomodation-type/accomodation-type'
import { JourneyPlaceInput } from '../../../lib/types/models/journey-place/journey-place-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../lib/utils/functions/misc.functions'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import { ControlledAdressInput } from '../../elements/inputs/address-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'
import JourneyPlaceOtoIndividualPlaces from './journey-place-oto-individual-places'
import { COLLECTIVE_ACCOMODATION_TYPES, journeyPlaceFormSchema } from './utils'

type Props = BaseFormProps<JourneyPlaceInput> & {
  journeyPlaceId?: number
}

const JourneyPlaceForm = ({ defaultValues, journeyPlaceId, ...validationButtonProps }: Props) => {
  const form = useForm<JourneyPlaceInput>({
    resolver: zodResolver(journeyPlaceFormSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const selectedAccomodation = form.watch('accomodation')
  const isAddressRequired = COLLECTIVE_ACCOMODATION_TYPES.includes(selectedAccomodation)
  const isOtoIndividual = form.watch('isOtoIndividual')
  const hasAgeLimit = form.watch('hasAgeLimit')

  useEffect(() => {
    if (form.formState.isSubmitted) {
      form.trigger('address')
    }
  }, [selectedAccomodation])

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: timezones } = useQuery({
    queryKey: ['timezones'],
    queryFn: () => getTimezones()
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <InnerBox className={'p-4 rounded-2xl flex flex-col gap-3'}>
          <Typography variant={'text'} color={tailwindColors.primary.DEFAULT}>
            Lieu
          </Typography>

          <div className={'flex gap-4 flex-row'}>
            <FormField
              control={form.control}
              name={'name'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput required {...field} type={'text'} label={'NOM du lieu'} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'countryId'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      required
                      searchable
                      {...field}
                      label={'Pays'}
                      options={countries?.data?.map((country) => ({
                        label: country.name,
                        value: country.id
                      }))}
                      selectedOption={conditionalRender(!!field.value, {
                        label: countries?.data?.find((a) => a.id === field.value)?.name || '',
                        value: field.value
                      })}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'accomodation'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      required
                      {...field}
                      label={"Type d'hébergement"}
                      options={Object.values(AccomodationEnum).map((option) => ({
                        label: accomodationEnumMapping[option],
                        value: option
                      }))}
                      selectedOption={getSelectedEnumOption(field.value, accomodationEnumMapping)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name={'timezone'}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    required
                    searchable
                    label={'Timezone'}
                    options={timezones?.map((timezone) => ({
                      label: timezone.name,
                      value: timezone.name
                    }))}
                    selectedOption={conditionalRender(!!field.value, {
                      label:
                        timezones?.find((timezone) => timezone.name === field.value)?.name ?? '',
                      value: field.value
                    })}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'address'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledAdressInput
                    required={isAddressRequired}
                    {...field}
                    label={'Adresse'}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'isOtoIndividual'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Checkbox
                    {...field}
                    checked={field.value}
                    label={'One to One individuel'}
                    onChange={(checked) => {
                      field.onChange(checked)
                      if (!checked) {
                        form.setValue('price', null)
                        form.setValue('hasAgeLimit', false)
                        form.setValue('mandatoryUnderAge', null)
                      }
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </InnerBox>

        {isOtoIndividual && (
          <>
            {journeyPlaceId && (
              <>
                <div className={'h-4'} />
                <JourneyPlaceOtoIndividualPlaces journeyPlaceId={journeyPlaceId} />
              </>
            )}

            <div className={'h-4'} />
            <InnerBox className={'p-4 rounded-2xl flex flex-col gap-3'}>
              <Typography variant={'text'} color={tailwindColors.primary.DEFAULT}>
                {"Prix de l'option Accueil-transfert"}
              </Typography>

              <FormField
                control={form.control}
                name={'price'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <ControlledInput
                        required
                        {...field}
                        icon={EuroIcon}
                        type={'number'}
                        label={'Prix'}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'hasAgeLimit'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Checkbox
                        {...field}
                        checked={field.value}
                        labelClassName="font-semibold"
                        label={"Obligatoire en dessous d'un certain âge ?"}
                        onChange={(checked) => {
                          field.onChange(checked)
                          if (!checked) form.setValue('mandatoryUnderAge', null)
                          if (form.formState.isSubmitted) form.trigger('mandatoryUnderAge')
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'mandatoryUnderAge'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <ControlledInput
                        required={hasAgeLimit}
                        disabled={!hasAgeLimit}
                        {...field}
                        type={'number'}
                        label={'Âge'}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </InnerBox>
          </>
        )}

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default JourneyPlaceForm
